import { cn } from '@app/utils/cn';
import { useMovement } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import React from 'react';

import { MoveCms } from '../Icons';

export const enum Direction {
  TOP = 'top',
  RIGHT = 'right',
  LEFT = 'left',
  BOTTOM = 'bottom',
}

interface Props {
  isActive?: boolean;
  onResizeStart?: () => void;
  onResizing: (value: number) => void;
  onResizeEnd?: (value?: number) => void;
  position?: Direction;
  className?: string;
}

export const Resizer = React.memo(({ onResizing, onResizeStart, onResizeEnd, isActive, position = Direction.RIGHT, className }: Props) => {
  const resizeButton = React.useRef<HTMLButtonElement | null>(null);

  const isVertical = position === Direction.TOP || position === Direction.BOTTOM;
  const onMovement = React.useCallback(
    (offset: { xOffset: number; yOffset: number }) => {
      onResizing(isVertical ? offset.yOffset : offset.xOffset);
    },
    [isVertical, onResizing],
  );

  const onEndMovement = React.useCallback(
    (offset: { xOffset: number; yOffset: number }) => {
      if (!isDefined(onResizeEnd)) {
        return;
      }

      onResizeEnd(isVertical ? offset.yOffset : offset.xOffset);
    },
    [onResizeEnd, isVertical],
  );

  useMovement(
    resizeButton,
    {
      onEndMovement,
      onMovement,
      onStartMovement: onResizeStart,
    },
    isActive,
  );

  return (
    <button
      type="button"
      className={cn(
        'absolute z-cmsResizer rounded-full border-none bg-none outline-none focus-visible:focus-outline',
        {
          '-translate-x-1/2 -translate-y-1/2': position === 'top' || position === 'left',
          'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2': position === 'bottom',
          'cursor-ns-resize': position === 'top' || position === 'bottom',
          'left-0 top-1/2': position === 'left',
          'left-1/2 top-0': position === 'top',
          'right-0 -translate-y-1/2 translate-x-1/2': position === 'right',
          'rotate-90 cursor-ew-resize': position === 'left' || position === 'right',
          'top-1/2': position === 'left' || position === 'right',
        },
        className,
      )}
      ref={resizeButton}
    >
      <MoveCms className="size-6 text-cmsPink-20" />
    </button>
  );
});
